/**
 * EPIC-048 / 48.4 — Onglet « Menu favoris » du profil utilisateur (§28.F.3).
 *
 * Gère les 3 emplacements de favoris pour l'utilisateur courant :
 *   - rename (PATCH {label})
 *   - moveUp / moveDown (1 PATCH si cible libre, 2 PATCH parallèles si swap)
 *   - delete (DELETE)
 *
 * Hydratation : 1 GET au `connect()`, ré-hydratation sur événement
 * `favorites:changed` (broadcast par 48.3 et par les actions locales).
 *
 * Garde-fous :
 *   - compte non `fully_active` §28.H → encart « Activez votre compte »
 *   - compte `archived_readonly` §28.I → slots affichés en lecture seule
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";
import { queryParamsToSearch } from "../services/favorites/normalize.js";

const MAX_SLOTS = 3;

export default class FavoritesManagerController extends BaseController {
  static targets = ["container", "loading", "prospectBlock"];

  async connect() {
    this.favorites = [];
    this.readonly = false;

    this.refresh = this.refresh.bind(this);
    window.addEventListener("favorites:changed", this.refresh);

    try {
      const me = await api.getMeCached();
      if (me?.organization?.onboardingStage !== "fully_active") {
        this.showProspectBlock();
        return;
      }
    } catch (_e) {
      this.showProspectBlock();
      return;
    }

    try {
      const state = await api.getLifecycle();
      this.readonly = state?.lifecycleStatus === "archived_readonly";
    } catch (_e) {
      // Best-effort : on continue en mode édition si l'appel échoue.
    }

    await this.refresh();
  }

  disconnect() {
    window.removeEventListener("favorites:changed", this.refresh);
  }

  async refresh() {
    let payload;
    try {
      payload = await api.get("/me/favorites");
    } catch (_e) {
      this.showError("Impossible de charger vos favoris.");
      return;
    }
    this.favorites = Array.isArray(payload?.favorites) ? payload.favorites : [];
    this.favorites.sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    this.render();
  }

  render() {
    if (!this.hasContainerTarget) return;

    while (this.containerTarget.firstChild) {
      this.containerTarget.removeChild(this.containerTarget.firstChild);
    }

    const maxOccupied = this.favorites.length > 0
      ? Math.max(...this.favorites.map((f) => f.position ?? 0))
      : -1;

    for (let position = 0; position < MAX_SLOTS; position++) {
      const fav = this.favorites.find((f) => f.position === position);
      const node = fav
        ? this.buildOccupiedSlot(fav, position, maxOccupied)
        : this.buildEmptySlot(position);
      if (node) this.containerTarget.appendChild(node);
    }
  }

  buildOccupiedSlot(fav, position, maxOccupied) {
    const fragment = this.cloneTemplate("tpl-favorite-slot-occupied");
    const root = fragment.firstElementChild;
    if (!root) return null;
    root.dataset.position = String(position);
    root.dataset.id = fav.id;

    const labelInput = root.querySelector('[data-slot="label"]');
    if (labelInput) labelInput.value = fav.label ?? "";

    const urlNode = root.querySelector('[data-slot="url"]');
    if (urlNode) {
      urlNode.textContent = `${fav.routePath}${queryParamsToSearch(fav.queryParams || {})}`;
    }

    const moveUp = root.querySelector('[data-favorites-manager-target="moveUp"]');
    if (moveUp) {
      const atTop = position === 0;
      moveUp.disabled = atTop;
      moveUp.setAttribute("aria-disabled", atTop ? "true" : "false");
    }
    const moveDown = root.querySelector('[data-favorites-manager-target="moveDown"]');
    if (moveDown) {
      const atBottom = position >= maxOccupied;
      moveDown.disabled = atBottom;
      moveDown.setAttribute("aria-disabled", atBottom ? "true" : "false");
    }

    if (this.readonly) {
      root.querySelectorAll("button").forEach((btn) => {
        btn.hidden = true;
      });
      if (labelInput) labelInput.readOnly = true;
    }

    return root;
  }

  buildEmptySlot(position) {
    const fragment = this.cloneTemplate("tpl-favorite-slot-empty");
    const root = fragment.firstElementChild;
    if (!root) return null;
    root.dataset.position = String(position);
    return root;
  }

  showProspectBlock() {
    if (this.hasContainerTarget) this.containerTarget.hidden = true;
    if (this.hasProspectBlockTarget) this.prospectBlockTarget.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  async renameSlot(event) {
    event.preventDefault();
    if (this.readonly) return;
    const root = event.currentTarget.closest("[data-id]");
    if (!root) return;
    const id = root.dataset.id;
    const input = root.querySelector('[data-slot="label"]');
    const label = (input?.value || "").trim();
    if (label.length === 0) {
      this.showError("Le libellé est obligatoire.");
      return;
    }

    try {
      await api.patch(`/me/favorites/${id}`, { label });
      this.showSuccess("Libellé enregistré.");
      const local = this.favorites.find((f) => f.id === id);
      if (local) local.label = label;
      window.dispatchEvent(new CustomEvent("favorites:changed"));
    } catch (err) {
      this.showError(err?.message || "Impossible d'enregistrer le libellé.");
    }
  }

  async moveUp(event) {
    if (this.readonly) return;
    const root = event.currentTarget.closest("[data-id]");
    if (!root) return;
    const position = Number.parseInt(root.dataset.position, 10);
    if (position <= 0) return;
    await this.swapOrMove(root.dataset.id, position, position - 1);
  }

  async moveDown(event) {
    if (this.readonly) return;
    const root = event.currentTarget.closest("[data-id]");
    if (!root) return;
    const position = Number.parseInt(root.dataset.position, 10);
    if (position >= MAX_SLOTS - 1) return;
    await this.swapOrMove(root.dataset.id, position, position + 1);
  }

  async swapOrMove(idA, posA, posB) {
    const favB = this.favorites.find((f) => f.position === posB);

    if (favB) {
      const results = await Promise.allSettled([
        api.patch(`/me/favorites/${idA}`, { position: posB }),
        api.patch(`/me/favorites/${favB.id}`, { position: posA }),
      ]);
      const failed = results.find((r) => r.status === "rejected");
      if (failed) {
        this.showError("Réordonnancement partiel — rechargement.");
        await this.refresh();
        window.dispatchEvent(new CustomEvent("favorites:changed"));
        return;
      }
    } else {
      try {
        await api.patch(`/me/favorites/${idA}`, { position: posB });
      } catch (err) {
        this.showError(err?.message || "Impossible de réordonner les favoris.");
        await this.refresh();
        return;
      }
    }

    this.showSuccess("Ordre mis à jour.");
    await this.refresh();
    window.dispatchEvent(new CustomEvent("favorites:changed"));
  }

  async delete(event) {
    if (this.readonly) return;
    const root = event.currentTarget.closest("[data-id]");
    if (!root) return;
    const id = root.dataset.id;
    const fav = this.favorites.find((f) => f.id === id);
    const label = fav?.label || "ce favori";

    const ok = await this.confirm(
      `« ${label} » sera supprimé de vos favoris.`,
      {
        title: "Supprimer ce favori ?",
        confirmLabel: "Supprimer",
        cancelLabel: "Annuler",
      },
    );
    if (!ok) return;

    try {
      await api.delete(`/me/favorites/${id}`);
      this.showSuccess("Favori supprimé.");
      window.dispatchEvent(new CustomEvent("favorites:changed"));
      await this.refresh();
    } catch (err) {
      this.showError(err?.message || "Impossible de supprimer ce favori.");
    }
  }
}
